import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from './modal'
import { Spinner } from './spinner'
import { NumericInput } from './numeric-input'
import { PositionProgress } from './position-progress'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { useRefreshActivity } from '@/lib/activity-feed'
import { impactWarns } from '@/lib/price-impact'
import type { PositionSnapshot } from '@/lib/api-types'
import { formatPct, formatSol, formatTokenAmount, formatUsd, LAMPORTS_PER_SOL } from '@/lib/format'

const SOL_MINT = 'So11111111111111111111111111111111111111112'

function toRaw(value: string, decimals: number): bigint | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match || (match[1] === '' && !match[2])) return null
  const raw = BigInt((match[1] || '0') + (match[2] ?? '').slice(0, decimals).padEnd(decimals, '0'))
  return raw > 0n ? raw : null
}

/** Sells part or all of a private position; proceeds route back through Relay. */
export function PositionSellModal({ position, onClose }: { position: PositionSnapshot | null; onClose: () => void }) {
  const trpc = useTRPC()
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [progressId, setProgressId] = useState<string | null>(null)
  useEffect(() => {
    if (position) { setAmount(''); setError(null); setProgressId(null) }
  }, [position?.id])
  const decimals = position?.token.decimals ?? 0
  const balance = position?.tokenBalance ?? 0
  const sellUsd = position?.priceUsd ? (Number(amount) || 0) * position.priceUsd : undefined
  const symbol = (position?.token.symbol ?? 'token').toUpperCase()
  const raw = position ? toRaw(amount, decimals) : null
  const full = position ? raw !== null && raw >= BigInt(position.tokenBalanceRaw) : false
  const estimate = useQuery({
    ...trpc.swap.estimate.queryOptions({ inputMint: position?.token.address ?? '', outputMint: SOL_MINT, amountRaw: raw?.toString() ?? '0' }),
    enabled: position !== null && raw !== null && progressId === null,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: false,
  })
  const outSol = estimate.data ? Number(estimate.data.outAmountRaw) / LAMPORTS_PER_SOL : undefined
  const refreshActivity = useRefreshActivity()
  const sell = useMutation(trpc.position.sell.mutationOptions({ onSuccess: (res) => { setProgressId(res.id); refreshActivity(['position']) }, onError: (err) => setError(errorMessage(err)) }))

  function submit() {
    if (!position || !raw) return setError('Enter an amount.')
    if (Number(amount) > balance + 1e-12) return setError('Amount is more than the position holds.')
    sell.mutate({ id: position.id, amountRaw: full ? position.tokenBalanceRaw : raw.toString() })
  }
  const pct = (n: number) => setAmount(n === 100 ? (Number(position?.tokenBalanceRaw ?? 0) / 10 ** decimals).toString() : ((balance * n) / 100).toFixed(Math.min(decimals, 6)).replace(/\.?0+$/, ''))

  return (
    <Modal
      open={position !== null}
      onClose={onClose}
      title={`Sell ${symbol}`}
      footer={
        progressId ? (
          <button type="button" className="btn btn-secondary w-full" onClick={onClose}>Close</button>
        ) : (
          <button type="button" className="btn btn-primary w-full" disabled={sell.isPending || !raw} onClick={submit}>
            {sell.isPending ? <Spinner /> : full ? 'Sell all and close' : 'Sell'}
          </button>
        )
      }
    >
      {position ? (
        <div className={`grid gap-3 ${progressId ? 'pointer-events-none opacity-60' : ''}`} aria-busy={progressId !== null}>
          <div className="card grid gap-3 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Amount</span>
              <span className="flex gap-2 text-xs text-accent">
                {[25, 50, 100].map((n) => (
                  <button key={n} type="button" onClick={() => pct(n)}>{n === 100 ? 'All' : `${n}%`}</button>
                ))}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <NumericInput className="w-0 min-w-0 flex-1 bg-transparent text-3xl font-semibold outline-none placeholder:text-muted" placeholder="0" value={amount} onChange={setAmount} readOnly={progressId !== null} />
              <span className="pill bg-surface text-sm">{symbol}</span>
            </div>
            <div className="flex justify-between text-xs text-muted">
              <span>{position.priceUsd ? formatUsd((Number(amount) || 0) * position.priceUsd) : '$0.00'}</span>
              <span>Holds {formatTokenAmount(balance, symbol)}</span>
            </div>
          </div>
          <div className="card flex items-center justify-between gap-3 p-4 text-sm">
            <span className="text-muted">You receive (est.)</span>
            <span className="font-medium tabular-nums">{estimate.isFetching && !estimate.data ? <Spinner className="size-4" /> : outSol !== undefined ? `≈ ${formatSol(outSol)} SOL` : '–'}</span>
          </div>
          {impactWarns(estimate.data?.priceImpactPct, sellUsd) ? (
            <p className="text-xs text-warning">Price impact {formatPct(estimate.data.priceImpactPct, false)} on this size.</p>
          ) : null}
          {estimate.isError ? <p className="text-xs text-negative">No route found for this size.</p> : null}
          {full ? <p className="text-xs text-muted">Selling everything also returns the leftover SOL and closes the position.</p> : null}
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      ) : null}
      {progressId ? (
        <div className="card mt-3 p-4"><PositionProgress positionId={progressId} mode="sell" /></div>
      ) : null}
    </Modal>
  )
}
